// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — carries out the AYNI chatbot's agent commands on this site's globe
 * (AYNI 3D's `docs/AI_CHATBOT_PLAN.md` §10.3, and its research and
 * education contract, `docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md`).
 * The engine (`src/ayni3d/`) validates every tool call before it gets here;
 * this module maps each command onto the site's own seams, through
 * `XrChatHost`, which `main.ts` implements. The desktop apps' commands
 * answer in their shape (`AgentForwarderResult`, with the new observation
 * after a change); the research, general and education commands answer an
 * `AgentToolOutcome` (`xrChatOutcomes.ts`): ok with values, method and
 * cautions, pending, unavailable with the module's reason, or locked.
 *
 * Who may do what (contract §3, §5; owner decisions 2026-10-07):
 *   - a research command needs the research tools (`data` and `research`);
 *     without them it is `locked: 'plan'`, said in one neutral sentence;
 *   - in a meeting's audience every other command that is not a read is
 *     `locked: 'meeting'`;
 *   - numbers the chatbot computes go to a research record only when one is
 *     already recording, marked as the chatbot's (`asAssistant`); never the
 *     chat's text.
 *
 * Commands outside the mode's set answer `UNSUPPORTED`; the engine never
 * offers them to the model, so this is only a safety net.
 */
import type { AgentCatalogResult, AgentCommand, AgentForwarderResult, AgentObservation, AgentToolOutcome, AgentYearSpan } from '../ayni3d/shared/protocol'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { AiChatMode } from '../ayni3d/shared/ai-chat-protocol'
import type { Dataset } from '../types'
import { dataKindOf, themesOf } from '../services/datasetTaxonomy'
import { buildSmartIndex, smartSearch, type SmartIndex } from '../services/smartSearch'
import { MAX_PINS, type Pin } from '../services/measure/pins'
import { summarizeSeries } from '../services/measure/series'
import { seriesWhenExtremes } from '../services/measure/whenExtremes'
import { MIN_TREND_POINTS, pinCorrelation, pinEmergence, pinTrend, seriesPoints } from '../services/research/pinStats'
import { MK_MAX_POINTS } from '../services/research/stats'
import { snapToFrame } from '../services/research/frameTime'
import { asAssistant, trail } from '../services/research/researchTrail'
import { periodMs, type EmergenceView } from '../ui/pinTrendUI'
import { XR_CHAT_COMMANDS, XR_RESEARCH_COMMANDS } from './xrChatScope'
import {
  CORRELATION_REFUSAL_TEXT,
  EMERGENCE_REFUSAL_TEXT,
  LOCK_TEXT,
  TREND_REFUSAL_TEXT,
  citeOutcome,
  correlationOutcome,
  emergenceOutcome,
  locked,
  trendOutcome,
  unavailable,
  type ChatPin,
} from './xrChatOutcomes'
import type { ChatAnalysis, ChatAnalysisRequest } from '../ui/analyzeChat'
import { featuresQuery, type XrChatFeatureSource } from './xrChatFeatures'
import {
  satellitesDatasets,
  satellitesEvents,
  satellitesPasses,
  satellitesShow,
  satellitesSources,
  type XrChatSatelliteContext,
  type XrChatSatellites,
} from './xrChatSatellites'
import { solarCommand, type XrChatSolar } from './xrChatSolar'

/** What the executor needs from the page. */
export interface XrChatHost {
  /** The catalogue the browse panel lists. */
  datasets(): readonly Dataset[]
  currentDataset(): Dataset | null
  /** Load a dataset the way the browse panel does; resolves once it is on screen (false: it did not load). */
  loadDataset(id: string): Promise<boolean>
  /** Fly the primary globe (and the VR globe, when a session is live). Altitude in km. */
  flyTo(lat: number, lon: number, altitudeKm?: number): Promise<void>
  /** The primary globe's centre and MapLibre zoom. */
  view(): { lat: number; lon: number; zoom: number } | null
  /** The loaded video's state, or null without one. */
  playback(): { isPlaying: boolean; currentTime: number; duration: number; rate: number } | null
  /** Play/pause as the transport button does. */
  togglePlay(): void
  seekSeconds(seconds: number): void
  setRate(rate: number): void
  /** The date seek the date track and the Khipu player use. */
  seekToDate(isoDate: string): { success: boolean; message: string }
  /** The stream's time axis from its `.dsa`, when it has one. */
  timeline(): { startMs: number; endMs: number; currentMs: number | null } | null
  /** The value under a point of a value-encoded dataset, or null. */
  probe(lat: number, lon: number): { value: number; units?: string; noData: boolean; quantisationStep?: number; uncertainty?: number; uncertaintyComplete?: boolean } | null
  /** True in a meeting's audience: the presenter chooses, the chatbot only answers. */
  locked(): boolean
  /** AYNI: true while a meeting's presenter lets the audience explore (the globe moves for them). */
  exploring?(): boolean
  /** The package a stream needs that the plan lacks, by its name for people; null when it may load (`streamLockedBy`). */
  streamLock?(dataset: Dataset): Promise<string | null>
  /** Whether the plan includes the data tools, which reading values needs (`dataToolsAllowed`). */
  dataTools?(): boolean
  /** The Data tab's measurement pins (`PinStore` in main.ts); absent where the page has none. */
  pins?: XrChatPins
  /**
   * AYNI: the Analyze panel's statistics on the frame on screen
   * (`analyzeChat.ts`); `show` opens Analyze on the region measured.
   */
  analysis?(request: ChatAnalysisRequest, show: boolean): ChatAnalysis
  /** AYNI: the Research environment's tools (`analyzeChat.ts`); absent where the page has none. */
  research?: XrChatResearch
  /** AYNI: "Cite this view" for the dataset on screen; null without one. */
  cite?(format: 'text' | 'bibtex' | 'ris'): { citation: string; viewLink: string } | null
  /** AYNI: what a reading means on the dataset on screen, and at a place (`explain.value`). */
  explain?(point: { lat: number; lon: number } | null): AgentToolOutcome
  /** AYNI (DSA 1.8): the main globe's vector layers, for `features.query`; null when it draws none. */
  features?(): XrChatFeatureSource | null
  /** AYNI (satellites ↔ catalog plan): the Satellites layer, for the `satellites.*` commands; absent where the page has none. */
  satellites?: XrChatSatellites
  /** AYNI (owner, 2026-11-05): Solar System mode, for the `solar.*` commands (xrChatSolar.ts); absent where the page has none. */
  solar?: XrChatSolar
}

/** What the executor needs of the Data tab's pins. */
export interface XrChatPins {
  /** Whether the dataset on screen can hold pins: 'release' and 'encoded' carry values. */
  readability(): 'none' | 'picture' | 'release' | 'encoded' | 'plan'
  list(): readonly Pin[]
  /** False when the store is full. */
  add(lat: number, lon: number): boolean
  /** The most pins the plan allows now (Plus 2); MAX_PINS when absent. */
  limit?(): number
  clear(): void
  /** Pins on the highest and lowest value on screen; how many were placed. */
  markExtremes(): number
  /** Show the pins' rows and graph (the Data tab). */
  show(): void
}

/** AYNI: the research tools as the chatbot drives them (`analyzeChat.ts` implements them on the panel). */
export interface XrChatResearch {
  /** The plan includes the research tools (`researchToolsAllowed`). */
  allowed(): boolean
  /** The pins as the Data tab shows them, with its emergence settings; null without the Data tab. */
  stats(): { pins: readonly Pin[]; dated: boolean; emergence: EmergenceView; units: string | undefined } | null
  baselineHold(request: { frame: 'current' } | { period: 'default' | AgentYearSpan }): AgentToolOutcome
  baselineStatus(): AgentToolOutcome
  baselineClear(): AgentToolOutcome
  changeShow(mode: 'off' | 'difference' | 'percent' | 'sigma'): AgentToolOutcome
  compare(command: Extract<AgentCommand, { type: 'compare.run' }>): AgentToolOutcome
  threshold(command: Extract<AgentCommand, { type: 'threshold.area' }>): AgentToolOutcome
  hovmoller(command: Extract<AgentCommand, { type: 'hovmoller.summary' }>): AgentToolOutcome
  methods(): AgentToolOutcome
  figure(kind?: 'globe' | 'equal-earth'): AgentToolOutcome
  bundle(): AgentToolOutcome
}

/** Who the chatbot is helping now: set by `ayniChat.ts` before each turn. */
export interface XrChatContext {
  mode: AiChatMode | null
  language: AiChatLanguage
}

const EARTH_RADIUS_KM = 6371
/** MapLibre's whole-globe zoom on this site (`MIN_ZOOM` in mapRenderer.ts). */
const WHOLE_GLOBE_ZOOM = 0.5
const MAX_ZOOM = 8
const MAX_PROBE_POINTS = 20
const SEARCH_DESCRIPTION_CHARS = 300
const DAY_MS = 86_400_000

/** AYNI zoom (1: the whole sphere, larger is closer) as MapLibre zoom. */
export function mapZoomFor(ayniZoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(WHOLE_GLOBE_ZOOM, WHOLE_GLOBE_ZOOM + Math.log2(ayniZoom)))
}

/** MapLibre zoom as AYNI zoom. */
export function ayniZoomFor(mapZoom: number): number {
  return Math.round(2 ** (mapZoom - WHOLE_GLOBE_ZOOM) * 100) / 100
}

/** The altitude `flyTo` takes for a MapLibre zoom (its own conversion, inverted). */
export function altitudeKmFor(mapZoom: number): number {
  return (EARTH_RADIUS_KM * 2) / 2 ** mapZoom
}

/**
 * AYNI (owner on Quest, 2026-11-05): how long a turn waits for the globe to
 * arrive. A flight takes 2.5 s; one that never reports its end (the map's,
 * whose frames stop in a headset) must not hold the answer. Past this the
 * turn goes on and the globe arrives when it does.
 */
export const FLY_WAIT_MS = 6_000

/** Waits for `work`, but no longer than `ms`. */
export async function within(work: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([work.catch(() => {}), new Promise<void>((resolve) => { timer = setTimeout(resolve, ms) })])
  clearTimeout(timer)
}

const iso = (ms: number): string => new Date(ms).toISOString()

/** A value to the precision the data carries (one luma step), else three decimals. */
function roundTo(value: number, step?: number): number {
  const decimals = step && step > 0 ? Math.max(0, Math.min(6, Math.ceil(-Math.log10(step)))) : 3
  return Number(value.toFixed(decimals))
}

function clip(text: string | undefined, max: number): string | undefined {
  if (!text) return undefined
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}

/** A catalogue row as the engine reads it: its kind and themes as categories, so "projection" finds projections. */
export function toCatalogResult(dataset: Dataset): AgentCatalogResult {
  return {
    id: dataset.id,
    title: dataset.title,
    description: clip(dataset.abstractTxt, SEARCH_DESCRIPTION_CHARS),
    categories: [dataKindOf(dataset), ...themesOf(dataset)],
    keywords: dataset.tags?.slice(0, 12),
  }
}

/** Search by words over the catalogue, through the browse panel's own ranking. */
export class XrCatalogSearch {
  private index: SmartIndex | null = null
  private indexed: readonly Dataset[] | null = null

  constructor(private readonly datasets: () => readonly Dataset[]) {}

  search(query: string, limit: number): AgentCatalogResult[] {
    const all = this.datasets()
    if (this.indexed !== all) {
      this.index = buildSmartIndex(all)
      this.indexed = all
    }
    const hits = this.index ? smartSearch(this.index, query) : null
    if (!hits) return []
    const byId = new Map(all.map((d) => [d.id, d]))
    const results: AgentCatalogResult[] = []
    for (const hit of hits) {
      const dataset = byId.get(hit.id)
      if (dataset) results.push(toCatalogResult(dataset))
      if (results.length >= limit) break
    }
    return results
  }
}

const RESEARCH: ReadonlySet<string> = new Set(XR_RESEARCH_COMMANDS)

/**
 * The commands that change nothing anyone else sees: allowed in a meeting's
 * audience. `hovmoller.summary` with a line to set is not one.
 */
function isRead(command: AgentCommand): boolean {
  switch (command.type) {
    case 'catalog.search':
    case 'state.describe':
    case 'stats.trend':
    case 'stats.emergence':
    case 'stats.correlation':
    case 'baseline.status':
    case 'methods.get':
    case 'cite.view':
    case 'explain.value':
    case 'features.query':
    case 'satellites.sources':
    case 'satellites.datasets':
    case 'satellites.passes':
      return true
    case 'hovmoller.summary':
      return !command.from
    case 'satellites.events':
      return command.open === undefined
    default:
      return command.type.startsWith('luma.')
  }
}

/** Is this command one of the contract's (answered with an `AgentToolOutcome`)? */
const isOutcomeCommand = (type: string): boolean =>
  RESEARCH.has(type) || type === 'cite.view' || type === 'explain.value' || type === 'features.query' || type.startsWith('satellites.') || type.startsWith('solar.')

export class XrChatExecutor {
  private context: XrChatContext = { mode: null, language: 'en' }

  constructor(
    private readonly host: XrChatHost,
    private readonly catalog: XrCatalogSearch,
  ) {}

  /** Who the chatbot is helping on this turn (the server resolved it; `ayniChat.ts` passes it on). */
  setContext(context: XrChatContext): void {
    this.context = context
  }

  /** What the globe shows now, in the engine's terms. */
  observe(): AgentObservation {
    const dataset = this.host.currentDataset()
    const playback = this.host.playback()
    const view = this.host.view()
    const observation: AgentObservation = {
      media: {
        isPlaying: playback?.isPlaying ?? false,
        currentTime: playback?.currentTime ?? 0,
        duration: playback?.duration ?? 0,
        autoRotation: false,
        autoRotationSpeed: 0,
        volume: 1,
        playbackSpeed: playback?.rate ?? 1,
        sliceCount: 1,
      },
      canvas: {
        x: 0,
        y: 0,
        zoom: view ? ayniZoomFor(view.zoom) : 1,
        rotationX: 0,
        rotationY: 0,
        centerLongitudeDeg: view?.lon ?? 0,
        centerLatitudeDeg: view?.lat ?? 0,
      },
      capabilities: [],
    }
    if (dataset) {
      const row = toCatalogResult(dataset)
      observation.currentDataset = { id: row.id, title: row.title, description: row.description, categories: row.categories, keywords: row.keywords }
      const timeline = this.timeline(dataset, playback)
      if (timeline) observation.timeline = timeline
    }
    return observation
  }

  private timeline(
    dataset: Dataset,
    playback: ReturnType<XrChatHost['playback']>,
  ): AgentObservation['timeline'] {
    const axis = this.host.timeline()
    if (axis) return { start: iso(axis.startMs), end: iso(axis.endMs), now: axis.currentMs === null ? null : iso(axis.currentMs) }
    if (!dataset.startTime || !dataset.endTime) return undefined
    const start = Date.parse(dataset.startTime)
    const end = Date.parse(dataset.endTime)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return undefined
    const now = playback && playback.duration > 0 ? iso(start + (playback.currentTime / playback.duration) * (end - start)) : null
    return { start: iso(start), end: iso(end), now }
  }

  private done(result: unknown = undefined): AgentForwarderResult {
    return { success: true, result, observation: this.observe() }
  }

  private fail(code: string, message: string): AgentForwarderResult {
    return { success: false, error: { code, message } }
  }

  private outcome(result: AgentToolOutcome, observe = false): AgentForwarderResult {
    return observe ? { success: true, result, observation: this.observe() } : { success: true, result }
  }

  /** Who may do this now; a lock, or null to go ahead (contract §3, §5). */
  private lockFor(command: AgentCommand): AgentToolOutcome | null {
    const type = command.type
    if (RESEARCH.has(type) && !(this.host.research?.allowed() ?? false)) return locked('plan', LOCK_TEXT.plan)
    if (this.host.locked() && !isRead(command)) {
      if (type.startsWith('pins.') && this.host.dataTools?.()) return null
      if (type.startsWith('view.') && this.host.exploring?.()) return null
      return locked('meeting', LOCK_TEXT.meeting)
    }
    return null
  }

  async execute(command: AgentCommand): Promise<AgentForwarderResult> {
    if (!XR_CHAT_COMMANDS.has(command.type) && !isOutcomeCommand(command.type)) return this.fail('UNSUPPORTED', `${command.type} is not available on this site.`)
    const lock = this.lockFor(command)
    if (lock) {
      // The desktop apps' commands keep their old refusal in a meeting's audience.
      if (!isOutcomeCommand(command.type) && lock.status === 'locked' && lock.lock === 'meeting') {
        return this.fail('LOCKED', 'In this meeting the presenter controls the globe.')
      }
      return this.outcome(lock)
    }
    // Numbers the tools note while this runs are the chatbot's in a research record (never started here).
    return asAssistant(() => this.run(command))
  }

  private async run(command: AgentCommand): Promise<AgentForwarderResult> {
    switch (command.type) {
      case 'catalog.search': {
        const results = this.catalog.search(command.query, Math.min(command.limit ?? 8, 20))
        return { success: true, result: { count: results.length, results } }
      }
      case 'dataset.load': {
        const dataset = this.host.datasets().find((d) => d.id === command.datasetId)
        if (!dataset) return this.fail('NOT_FOUND', `No dataset has the id ${command.datasetId}.`)
        const lockedBy = (await this.host.streamLock?.(dataset)) ?? null
        if (lockedBy) {
          return this.fail('OUTSIDE_PLAN', `This stream is in the ${lockedBy} package, which the visitor's plan does not include. They can add it from the plans.`)
        }
        const loaded = await this.host.loadDataset(command.datasetId)
        return loaded ? this.done() : this.fail('LOAD_FAILED', 'The dataset did not load.')
      }
      case 'view.goto': {
        const altitude = command.zoom === undefined ? undefined : altitudeKmFor(mapZoomFor(command.zoom))
        await within(this.host.flyTo(command.latitude, command.longitude, altitude), FLY_WAIT_MS)
        return this.done()
      }
      case 'view.zoom': {
        const view = this.host.view()
        if (!view) return this.fail('UNAVAILABLE', 'The globe is not ready.')
        await within(this.host.flyTo(view.lat, view.lon, altitudeKmFor(mapZoomFor(command.zoom))), FLY_WAIT_MS)
        return this.done()
      }
      case 'media.play':
      case 'media.pause':
      case 'media.stop': {
        const playback = this.host.playback()
        if (!playback) return this.fail('NO_MEDIA', 'Nothing on screen plays.')
        const wantPlaying = command.type === 'media.play'
        if (playback.isPlaying !== wantPlaying) this.host.togglePlay()
        if (command.type === 'media.stop') this.host.seekSeconds(0)
        return this.done()
      }
      case 'media.seek': {
        const playback = this.host.playback()
        if (!playback || !(playback.duration > 0)) return this.fail('NO_MEDIA', 'Nothing on screen plays.')
        this.host.seekSeconds(Math.min(Math.max(0, command.time), playback.duration))
        return this.done()
      }
      case 'media.seekDate': {
        const outcome = this.host.seekToDate(command.date)
        if (!outcome.success) return this.fail('SEEK_FAILED', outcome.message)
        const observation = this.observe()
        return { success: true, result: { shown: observation.timeline?.now ?? command.date }, observation }
      }
      case 'media.speed': {
        if (!this.host.playback()) return this.fail('NO_MEDIA', 'Nothing on screen plays.')
        this.host.setRate(command.speed)
        return this.done()
      }
      case 'state.describe': {
        const observation = this.observe()
        return { success: true, result: observation, observation }
      }
      case 'luma.probe': {
        const result = this.read(command.lat, command.lon)
        if (result.available && !result.noData) trailNumbers('luma.probe', { lat: command.lat, lon: command.lon, ...result })
        return { success: true, result }
      }
      case 'luma.probeMany': {
        const result = command.points.slice(0, MAX_PROBE_POINTS).map((p) => ({ lat: p.lat, lon: p.lon, ...this.read(p.lat, p.lon) }))
        if (result.some((r) => r.available)) trailNumbers('luma.probeMany', { points: result })
        return { success: true, result }
      }
      case 'luma.stats':
      case 'luma.extremum':
      case 'luma.zonal':
      case 'luma.transect':
        return this.analysisCommand(command)
      case 'pins.place':
      case 'pins.read':
      case 'pins.clear':
      case 'pins.extremes':
        return this.pinCommand(command)
      case 'stats.trend':
      case 'stats.emergence':
      case 'stats.correlation':
        return this.outcome(this.pinStatistics(command))
      case 'baseline.hold':
      case 'baseline.status':
      case 'baseline.clear':
      case 'change.show':
      case 'compare.run':
      case 'threshold.area':
      case 'hovmoller.summary':
      case 'methods.get':
      case 'figure.open':
      case 'bundle.open':
        return this.outcome(this.researchCommand(command))
      case 'cite.view': {
        const format = command.format ?? 'text'
        const cited = this.host.cite?.(format) ?? null
        return this.outcome(cited ? citeOutcome(format, cited.citation, cited.viewLink) : unavailable('no-dataset', 'There is no dataset on screen to cite.'))
      }
      case 'explain.value': {
        if (!this.host.explain) return this.outcome(unavailable('unavailable', 'Readings cannot be explained on this page.'))
        // Reading the data is the data tools'.
        if (this.host.dataTools && !this.host.dataTools()) return this.outcome(unavailable('plan', 'Reading values needs a plan with the data tools.'))
        const point = command.lat !== undefined && command.lon !== undefined ? { lat: command.lat, lon: command.lon } : null
        const result = this.host.explain(point)
        if (point && result.status === 'ok') trailNumbers('explain.value', { ...(result.values.point as Record<string, unknown> | undefined) })
        return this.outcome(result)
      }
      case 'features.query': {
        // The same records the inspect popup shows anyone: no plan check.
        const out = featuresQuery(this.host.features?.() ?? null, command)
        if (out.status === 'ok') trailOutcome('features.query', `chat-features|${JSON.stringify(command).slice(0, 160)}`, out, `Records read: ${String(out.values.matched)} of ${String(out.values.total)} in ${String(out.values.layer)}, by ${String(out.values.sortedBy)}`)
        return this.outcome(out)
      }
      case 'satellites.sources':
      case 'satellites.datasets':
      case 'satellites.show':
      case 'satellites.passes':
      case 'satellites.events':
        return this.satelliteCommand(command)
      case 'solar.open':
      case 'solar.visit':
      case 'solar.view':
      case 'solar.close':
        // AYNI (owner, 2026-11-05): Solar System mode; what the globe shows changes, so the observation comes back.
        return this.outcome(await solarCommand(this.host.solar ?? null, command), true)
      default:
        return this.fail('UNSUPPORTED', `${command.type} is not available on this site.`)
    }
  }

  /**
   * The satellites ↔ catalog graph, read from the Satellites layer
   * (`xrChatSatellites.ts`). What the globe shows changes only with
   * `satellites.show` and an event opened; a change brings the observation.
   */
  private async satelliteCommand(command: Extract<AgentCommand, { type: `satellites.${string}` }>): Promise<AgentForwarderResult> {
    const sats = this.host.satellites
    if (!sats) return this.outcome(unavailable('no-layer', 'This page has no Satellites layer.'))
    const axis = this.host.timeline()
    const ctx: XrChatSatelliteContext = {
      dataset: this.host.currentDataset(),
      datasets: () => this.host.datasets(),
      frameMs: axis?.currentMs ?? null,
      axis: axis ? { startMs: axis.startMs, endMs: axis.endMs } : null,
      streamLock: this.host.streamLock ? (dataset) => this.host.streamLock!(dataset) : undefined,
      language: this.context.language,
    }
    switch (command.type) {
      case 'satellites.sources':
        return this.outcome(await satellitesSources(sats, ctx, command))
      case 'satellites.datasets':
        return this.outcome(await satellitesDatasets(sats, ctx, command))
      case 'satellites.show':
        return this.outcome(await satellitesShow(sats, command), true)
      case 'satellites.passes':
        return this.outcome(satellitesPasses(sats, ctx))
      case 'satellites.events':
        return this.outcome(satellitesEvents(sats, ctx, command), command.open !== undefined)
    }
  }

  /** Region statistics, extremes and profiles, as the Analyze panel computes them; shown there unless the presenter decides. */
  private analysisCommand(command: Extract<AgentCommand, { type: 'luma.stats' | 'luma.extremum' | 'luma.zonal' | 'luma.transect' }>): AgentForwarderResult {
    if (!this.host.analysis) return this.fail('UNAVAILABLE', 'Statistics are not available on this page.')
    const request: ChatAnalysisRequest = command.type === 'luma.stats'
      ? { kind: 'stats', bounds: command.bounds, threshold: command.threshold }
      : command.type === 'luma.extremum'
        ? { kind: 'extremum', which: command.kind ?? 'max', bounds: command.bounds }
        : command.type === 'luma.zonal'
          ? { kind: 'zonal', bounds: command.bounds, maxRows: command.maxRows }
          : { kind: 'transect', from: command.from, to: command.to, samples: command.samples }
    const show = !this.host.locked()
    const result = this.host.analysis(request, show)
    // A region shown in Analyze is noted by the panel itself; the rest here.
    if (result.available && !(show && command.type === 'luma.stats')) trailNumbers(command.type, { request, ...result })
    return { success: true, result }
  }

  private pinCommand(command: Extract<AgentCommand, { type: `pins.${string}` }>): AgentForwarderResult {
    const pins = this.host.pins
    if (!pins) return this.fail('UNAVAILABLE', 'Measurement pins are not available on this page.')
    if (command.type === 'pins.read') {
      // AYNI (owner on Quest, 2026-11-05: "show the graph over time" was not understood): reading the
      // pins also opens the Data panel, where their values over time are drawn as a graph.
      if (!pins.list().length) return { success: true, result: this.describePins(pins) }
      pins.show()
      return { success: true, result: { ...this.describePins(pins), shown: 'The Data panel is open now, with the pins\' values over time drawn as a graph.' } }
    }
    if (command.type === 'pins.clear') {
      pins.clear()
      return { success: true, result: { pins: 0 } }
    }
    const canHold = pins.readability()
    if (canHold === 'none') return this.fail('NO_DATASET', 'Load a dataset first: pins follow the values of the dataset on screen.')
    if (canHold === 'picture') return this.fail('NO_VALUES', 'This dataset is a picture: it carries no values for pins to follow.')
    if (canHold === 'plan') return this.fail('OUTSIDE_PLAN', 'Pins need a plan with the data tools.')
    if (command.type === 'pins.extremes') {
      if (!pins.markExtremes()) return this.fail('NO_VALUES', 'The highest and lowest values could not be found on this frame yet.')
      pins.show()
      return { success: true, result: this.describePins(pins) }
    }
    if (command.replace) pins.clear()
    let placed = 0
    for (const point of command.points) {
      if (!pins.add(point.lat, point.lon)) break
      placed++
    }
    if (placed) pins.show()
    const notPlaced = command.points.length - placed
    const playing = this.host.playback()?.isPlaying ?? false
    const most = pins.limit?.() ?? MAX_PINS
    const planNote = most < MAX_PINS ? ' The plan allows this many; larger plans allow ' + MAX_PINS + '.' : ''
    return {
      success: placed > 0,
      ...(placed ? {} : { error: { code: 'PINS_FULL', message: `There are already ${most} pins, the most there can be.${planNote} Clear them or replace them.` } }),
      result: {
        placed,
        ...(notPlaced ? { notPlaced, why: `at most ${most} pins${planNote}` } : {}),
        note: playing
          ? 'The pins collect values as the dataset plays.'
          : 'The pins have their value now; they collect values over time while the dataset plays.',
        ...this.describePins(pins),
      },
    }
  }

  /** Every pin: its id (for `stats.*`), where, its value now, and what its series has seen so far. */
  private describePins(pins: XrChatPins): Record<string, unknown> {
    const list = pins.list()
    if (!list.length) return { pins: [] }
    return {
      pins: list.map((pin, index) => {
        const reading = pin.reading as { value: number; noData: boolean; units?: string; quantisationStep?: number } | null
        const round = (value: number | null): number | null => (value === null ? null : roundTo(value, reading?.quantisationStep))
        const entry: Record<string, unknown> = {
          id: pin.id,
          pin: index + 1,
          lat: Math.round(pin.lat * 100) / 100,
          lon: Math.round(pin.lon * 100) / 100,
          ...(pin.tag ? { marks: pin.tag === 'max' ? 'highest' : 'lowest' } : {}),
          now: reading === null ? 'nothing to read here' : reading.noData ? 'no data here' : { value: round(reading.value), units: reading.units ?? null },
        }
        if (pin.series) {
          const summary = summarizeSeries(pin.series)
          const dated = pin.series.startMs !== 0
          entry.overTime = {
            lowest: round(summary.min),
            highest: round(summary.max),
            mean: round(summary.mean),
            latest: round(summary.latest),
            latestAt: summary.latestMs === null ? null : dated ? iso(summary.latestMs) : `${Math.round(summary.latestMs / 1000)} s into the video`,
            // AYNI (owner, 2026-10-18): when the highest and lowest were read (the first frame holding each; others share it).
            ...(() => {
              const when = seriesWhenExtremes(pin.series)
              if (!when) return {}
              const at = (ms: number) => (dated ? iso(ms) : `${Math.round(ms / 1000)} s into the video`)
              const end = (e: NonNullable<typeof when>['max']) => ({ time: at(e.timeMs), otherFramesWithTheSameValue: e.others, ...(e.at ? { lat: Math.round(e.at.lat * 100) / 100, lon: Math.round(e.at.lon * 100) / 100 } : {}) })
              return { highestAt: end(when.max), lowestAt: end(when.min) }
            })(),
            readSoFar: `${summary.coverage.sampled} of ${summary.coverage.total} time steps`,
          }
        }
        return entry
      }),
    }
  }

  /**
   * Trend, emergence and correlation on the Data tab's pins as the panel
   * shows them (`viewPins`: values, or their change from a held baseline),
   * with the panel's own functions and settings, so the numbers match its
   * Trend, Emergence and Correlation lines.
   */
  private pinStatistics(command: Extract<AgentCommand, { type: 'stats.trend' | 'stats.emergence' | 'stats.correlation' }>): AgentToolOutcome {
    const view = this.host.research?.stats() ?? null
    if (!view) return unavailable('no-pin', TREND_REFUSAL_TEXT['no-pin'])
    const named = (id: string): { pin: Pin; chat: ChatPin } | null => {
      const index = view.pins.findIndex((p) => p.id === id)
      if (index < 0) return null
      const pin = view.pins[index]
      // The name the viewer gave it (owner, 2026-10-09), else its own.
      const label = pin.name ?? (pin.tag === 'max' ? 'Highest value' : pin.tag === 'min' ? 'Lowest value' : `Pin ${index + 1}`)
      return { pin, chat: { id: pin.id, lat: pin.lat, lon: pin.lon, label } }
    }
    const units = view.units ?? null
    if (command.type === 'stats.trend') {
      const p = named(command.pin)
      if (!p) return unavailable('no-pin', TREND_REFUSAL_TEXT['no-pin'])
      if (!p.pin.series) return unavailable('no-series', TREND_REFUSAL_TEXT['no-series'])
      if (!view.dated || p.pin.series.startMs === 0) return unavailable('undated', TREND_REFUSAL_TEXT.undated)
      const n = seriesPoints(p.pin.series).values.length
      if (n < MIN_TREND_POINTS) return unavailable('short', TREND_REFUSAL_TEXT.short)
      if (n > MK_MAX_POINTS) return unavailable('tooMany', TREND_REFUSAL_TEXT.tooMany)
      const trend = pinTrend(p.pin.series)
      if (!trend) return unavailable('short', TREND_REFUSAL_TEXT.short)
      const out = trendOutcome(p.chat, trend, units)
      trailOutcome('stats.trend', `chat-trend|${p.pin.id}`, out, `Trend at ${p.chat.label}: ${trend.verdict}, OLS ${num(trend.ols.slope)} ${units ?? ''} per ${trend.unit}, Sen ${num(trend.mk.sen)}`)
      return out
    }
    if (command.type === 'stats.emergence') {
      const p = named(command.pin)
      if (!p) return unavailable('no-pin', EMERGENCE_REFUSAL_TEXT['no-pin'])
      if (!p.pin.series) return unavailable('no-series', EMERGENCE_REFUSAL_TEXT['no-series'])
      if (!view.dated || p.pin.series.startMs === 0) return unavailable('undated', EMERGENCE_REFUSAL_TEXT.undated)
      const period = command.baseline ? { startYear: command.baseline.from, endYear: command.baseline.to } : view.emergence.period
      const noise = command.noise ?? view.emergence.noise
      const r = pinEmergence(p.pin.series, { period: periodMs(period), noise, quantum: view.emergence.quantum })
      if (typeof r === 'string') return unavailable(r, EMERGENCE_REFUSAL_TEXT[r])
      const out = emergenceOutcome(p.chat, r, { units, ensemble: view.emergence.ensemble, noiseGiven: noise !== null && noise !== undefined, snap: (ms) => snapToFrame(ms) })
      trailOutcome('stats.emergence', `chat-emergence|${p.pin.id}`, out, `Emergence at ${p.chat.label}: ${r.emergence.emergedAt === null ? 'not emerged' : `from ${new Date(snapToFrame(r.emergence.emergedAt)).getUTCFullYear()}`} (baseline ${period.startYear}-${period.endYear})`)
      return out
    }
    const a = named(command.pinA)
    const b = named(command.pinB)
    if (!a || !b) return unavailable('no-pin', CORRELATION_REFUSAL_TEXT['no-pin'])
    if (!a.pin.series || !b.pin.series) return unavailable('no-series', CORRELATION_REFUSAL_TEXT['no-series'])
    const sa = a.pin.series
    const sb = b.pin.series
    if (sa.slots.length !== sb.slots.length || sa.startMs !== sb.startMs || sa.endMs !== sb.endMs) return unavailable('window', CORRELATION_REFUSAL_TEXT.window)
    const c = pinCorrelation(sa, sb)
    if (!c) return unavailable('short', CORRELATION_REFUSAL_TEXT.short)
    const stepYears = view.dated && sa.slots.length > 1 ? (sa.endMs - sa.startMs) / (sa.slots.length - 1) / (365.25 * DAY_MS) : null
    const out = correlationOutcome(a.chat, b.chat, c, stepYears)
    trailOutcome('stats.correlation', `chat-correlation|${a.pin.id}|${b.pin.id}`, out, `Correlation of ${a.chat.label} and ${b.chat.label}: r ${num(c.pearson.r)}, rho ${num(c.spearman.r)}, n ${c.n}`)
    return out
  }

  /** The Research environment's panel tools (`analyzeChat.ts`). */
  private researchCommand(command: Extract<AgentCommand, { type: 'baseline.hold' | 'baseline.status' | 'baseline.clear' | 'change.show' | 'compare.run' | 'threshold.area' | 'hovmoller.summary' | 'methods.get' | 'figure.open' | 'bundle.open' }>): AgentToolOutcome {
    const research = this.host.research
    if (!research) return unavailable('unavailable', 'The research tools are not available on this page.')
    switch (command.type) {
      case 'baseline.hold':
        return research.baselineHold(command.frame ? { frame: 'current' } : { period: command.period ?? 'default' })
      case 'baseline.status':
        return research.baselineStatus()
      case 'baseline.clear':
        return research.baselineClear()
      case 'change.show':
        return research.changeShow(command.mode)
      case 'compare.run':
        return research.compare(command)
      case 'threshold.area':
        return research.threshold(command)
      case 'hovmoller.summary': {
        const out = research.hovmoller(command)
        if (out.status === 'ok') trailOutcome('hovmoller.summary', 'chat-hovmoller', out, `Line over time: ${String(out.values.read)} of ${String(out.values.rows)} rows read`)
        return out
      }
      case 'methods.get':
        return research.methods()
      case 'figure.open':
        return research.figure(command.kind)
      case 'bundle.open':
        return research.bundle()
    }
  }

  /** One value, or why there is none; never a guess. */
  private read(lat: number, lon: number): { available: boolean; reason?: string; noData?: boolean; value?: number; units?: string | null; plusMinus?: number; plusMinusCovers?: string } {
    if (this.host.dataTools && !this.host.dataTools()) {
      return { available: false, reason: 'Reading values needs a plan with the data tools.' }
    }
    const reading = this.host.probe(lat, lon)
    if (!reading) return { available: false, reason: 'This dataset carries no values to read at that point.' }
    if (reading.noData) return { available: true, noData: true }
    const step = reading.quantisationStep
    const decimals = step && step > 0 ? Math.max(0, Math.min(6, Math.ceil(-Math.log10(step)))) : 3
    // AYNI: the ± and what it covers, so the model never states more precision than the data has (value error audit, 2026-10-19).
    const u = reading.uncertainty
    return {
      available: true,
      value: Number(reading.value.toFixed(decimals)),
      units: reading.units ?? null,
      ...(u !== undefined && u > 0
        ? { plusMinus: Number(u.toPrecision(2)), plusMinusCovers: reading.uncertaintyComplete === false ? 'rounding only; the streaming error is not stated, so the real error can be larger' : 'rounding and streaming (99 % of cells)' }
        : {}),
    }
  }
}

/** A number for the record's one-line summary (5 significant figures). */
const num = (v: number): string => (Number.isFinite(v) ? String(Number(v.toPrecision(5))) : '—')

/** A chatbot result with numbers, for a research record already recording (`asAssistant` marks it). */
function trailNumbers(command: string, data: Record<string, unknown>): void {
  trail({ type: 'stats', actor: 'assistant', coalesce: `chat|${command}|${JSON.stringify(data).slice(0, 200)}`, frame: true, data: { command, ...data, summary: `The chatbot read ${command.replace('luma.', '')}` } })
}

function trailOutcome(command: string, coalesce: string, out: AgentToolOutcome, summary: string): void {
  if (out.status !== 'ok') return
  trail({
    type: command === 'hovmoller.summary' ? 'hovmoller' : 'analysis',
    actor: 'assistant',
    coalesce,
    data: { kind: command.replace(/^stats\./, ''), command, ...out.values, method: out.method, cautions: out.cautions.map((c) => c.code), summary },
  })
}
